import { FC } from 'react';
import { useMutation } from '@apollo/client';

import { BankTransferStatus } from '__generated__/GQL';
import useAuthenticatedAction from 'common/hooks/useAuthenticatedAction';
import { Scope } from 'common/hooks/useAuthenticatedAction/types';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useErrorHandledMutation from 'common/hooks/useErrorHandledMutation';
import { StepProps } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import {
  BankTransferCreationStep,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';
import { useDevice } from 'helpers/device';

import PinCode from '../../components/PinCode';
import { CreateBankTransferDocument } from './graphql/createBankTransfer.gql';
import {
  CreateReceiptDocument,
  CreateReceiptMutation,
  CreateReceiptMutationVariables,
} from './graphql/createReceipt.gql';
import { CreateScheduledBankTransferDocument } from './graphql/createScheduledBankTransfer.gql';

const PerformExternalTransfer: FC<StepProps<BankTransferCreationStep>> = (
  props,
) => {
  const device = useDevice();
  const { companyProfileId, companyUserId } = useCompanyContext();
  const {
    bankTransfer: {
      amount,
      bankTransferRecipientId,
      fromBankAccountId,
      isInstant,
      label,
      receipt,
      recipientEmail,
      schedule,
      shouldNotifyRecipient,
    },
  } = useBankTransferContext('performExternal');

  const [createBankTransfer, { loading: createBankTransferLoading }] =
    useMutation(CreateBankTransferDocument);

  const [
    createScheduledBankTransfer,
    { loading: createScheduledBankTransferLoading },
  ] = useMutation(CreateScheduledBankTransferDocument);

  const [createReceipt, { loading: createReceiptLoading }] =
    useErrorHandledMutation<
      CreateReceiptMutation,
      CreateReceiptMutationVariables
    >(CreateReceiptDocument);

  const { isLoading: isAuthenticating, onPasscodeSubmit } =
    useAuthenticatedAction({
      onPasscodeSuccess: async (token) => {
        if (receipt) {
          await createReceipt({
            variables: {
              input: {
                bucketName: receipt.bucket,
                companyProfileId: companyProfileId ?? '',
                fileRef: receipt.fileRef,
                total: amount,
                url: receipt.url,
              },
            },
          });
        }

        try {
          if (schedule) {
            await createScheduledBankTransfer({
              context: {
                headers: {
                  'short-lived-token': token,
                },
              },
              variables: {
                input: {
                  amount,
                  bankAccountId: fromBankAccountId,
                  bankTransferRecipientId,
                  companyUserId,
                  documentBucket: receipt?.bucket,
                  documentFileRef: receipt?.fileRef,
                  label,
                  shouldNotifyRecipient,
                  ...schedule,
                  ...(shouldNotifyRecipient ? { recipientEmail } : {}),
                },
              },
            });
            props.goNext();
            return;
          }

          const { data: createBankTransferData } = await createBankTransfer({
            context: {
              headers: {
                'short-lived-token': token,
              },
            },
            variables: {
              input: {
                amount,
                bankAccountId: fromBankAccountId,
                bankTransferRecipientId,
                companyUserId,
                currency: 'EUR',
                deviceToken: device.token,
                isInstant: isInstant === true,
                receiptUrl: receipt?.url,
                shouldNotifyRecipient,
                userLabel: label,
                ...(shouldNotifyRecipient ? { recipientEmail } : {}),
              },
            },
          });

          const hasFailed =
            createBankTransferData?.createBankTransfer.bankTransfer?.status ===
            BankTransferStatus.Canceled;

          if (hasFailed) {
            props.goNext({
              key: 'failure',
              shouldReplaceCurrentStep: true,
            });
          } else {
            props.goNext();
          }
        } catch {
          props.goNext({
            key: 'failure',
            shouldReplaceCurrentStep: true,
          });
        }
      },
      scope: Scope.BANK_TRANSFERS_WRITE,
    });

  const loading =
    isAuthenticating ||
    createBankTransferLoading ||
    createScheduledBankTransferLoading ||
    createReceiptLoading;

  return (
    <>
      <TrackView pageName="PerformNewExternalTransfer" />
      <PinCode
        goBack={props.goBack}
        loading={loading}
        onPasscodeSubmit={onPasscodeSubmit}
      />
    </>
  );
};

export default PerformExternalTransfer;
